/**
 * Diffs form-state codeshares against backend and applies create/update/delete
 * operations per sector. Uses Promise.allSettled so partial failures don't
 * block the rest of the sync.
 */

import { useCallback } from 'react';
import {
  useLazyGetCodesharesByMasterFlightQuery,
  useCreateCodeshareMutation,
  useUpdateCodeshareMutation,
  useDeleteCodeshareMutation,
} from '../services/redux/api';
import type { CodeshareRecord } from '../services/redux/api';
import type { MasterFlightRecord } from '../utils/types';
import { extractCodesharesFromStations } from '../utils/codeshareHelpers';
import { customToast } from '../utils/toast';

interface SyncResult {
  success: boolean;
  created: number;
  updated: number;
  deleted: number;
  errors: string[];
}

export function useCodeshareSyncManager() {
  const [fetchCodeshares] = useLazyGetCodesharesByMasterFlightQuery();
  const [createCodeshare] = useCreateCodeshareMutation();
  const [updateCodeshare] = useUpdateCodeshareMutation();
  const [deleteCodeshare] = useDeleteCodeshareMutation();

  const fetchExistingCodeshares = useCallback(async (
    masterFlightId: string
  ): Promise<CodeshareRecord[]> => {
    try {
      const result = await fetchCodeshares(masterFlightId).unwrap();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error fetching existing codeshares:', error);
      return [];
    }
  }, [fetchCodeshares]);

  const syncCodeshares = useCallback(async (
    draft: MasterFlightRecord,
    savedFlightId: string
  ): Promise<SyncResult> => {
    const result: SyncResult = {
      success: true,
      created: 0,
      updated: 0,
      deleted: 0,
      errors: [],
    };

    try {
      const existingCodeshares = await fetchExistingCodeshares(savedFlightId);
      const existingBySector = new Map<string, CodeshareRecord>(
        existingCodeshares.map((cs) => [cs.sector, cs])
      );

      const newCodeshares = extractCodesharesFromStations(draft, savedFlightId);
      const newBySector = new Map(
        newCodeshares.map(cs => [cs.sector, cs])
      );

      const operations: Array<Promise<{ type: string; sector: string }>> = [];

      // Upsert: create new sectors, update existing ones
      for (const [sector, newData] of Array.from(newBySector.entries())) {
        const existing = existingBySector.get(sector);

        if (existing) {
          operations.push(
            updateCodeshare({
              id: existing._id,
              payload: {
                codeshareFlightNo: newData.codeshareFlightNo,
                csStartDate: newData.csStartDate,
                csEndDate: newData.csEndDate,
                frequency: newData.frequency,
                freqRate: newData.freqRate,
              }
            })
              .unwrap()
              .then(() => ({ type: 'update', sector }))
          );
        } else {
          operations.push(
            createCodeshare(newData)
              .unwrap()
              .then(() => ({ type: 'create', sector }))
          );
        }
      }

      // Delete sectors that were removed from the form
      for (const [sector, existing] of Array.from(existingBySector.entries())) {
        if (!newBySector.has(sector)) {
          operations.push(
            deleteCodeshare({ id: existing._id, masterflightRef: savedFlightId })
              .unwrap()
              .then(() => ({ type: 'delete', sector }))
          );
        }
      }

      const results = await Promise.allSettled(operations);

      results.forEach((promiseResult, index) => {
        if (promiseResult.status === 'fulfilled') {
          const { type } = promiseResult.value;
          if (type === 'create') result.created++;
          else if (type === 'update') result.updated++;
          else if (type === 'delete') result.deleted++;
        } else {
          result.success = false;
          result.errors.push(
            `Operation ${index + 1} failed: ${promiseResult.reason?.message || 'Unknown error'}`
          );
        }
      });

      if (result.errors.length > 0) {
        const successCount = result.created + result.updated + result.deleted;
        customToast.warning(
          `Codeshare sync partially completed. ${successCount} operations succeeded, ${result.errors.length} failed.`,
          { duration: 5000 }
        );
        console.error('Codeshare sync errors:', result.errors);
      }

      return result;
    } catch (error) {
      console.error('Critical error during codeshare sync:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      customToast.error('Failed to sync codeshares. Please try again or contact support.', { duration: 5000 });
      return result;
    }
  }, [createCodeshare, updateCodeshare, deleteCodeshare, fetchExistingCodeshares]);

  return { syncCodeshares, fetchExistingCodeshares };
}
